import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { TechnicalDocumentType } from "../../domain/review/technical-document-review.js";
import type { DocumentHold, HoldDocumentSummary } from "../../domain/review/document-hold.js";
import type { TechnicalCorrectionRequest } from "../../domain/review/technical-correction.js";
import { holdBanner, holdButton, loadActiveHold, openHoldModal, openHoldReleaseModal } from "../components/hold-ui.js";
import { applyDeepLinkFocus, pendingCorrectionsBanner } from "../components/correction-request-ui.js";

/**
 * Integração das páginas de elaboração END (LP, ME, PM, US-Convencional) com
 * o Painel de Engenharia: HOLD (botão, modal, banner, bloqueio), correções
 * por item (cards + MARCAR COMO CORRIGIDO + deep link) e corretor ortográfico
 * antes de salvar/validar. Cada página só chama estas funções.
 */

export interface NdtEngineeringState {
  hold: DocumentHold | null;
  corrections: TechnicalCorrectionRequest[];
}

function ctx(clientId?: string | null): any {
  const c = contextStore.get();
  return { activeClientId: clientId || c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
}

export async function loadNdtEngineeringState(documentType: TechnicalDocumentType, report: { id: string; clientId: string } | null): Promise<NdtEngineeringState> {
  if (!report) return { hold: null, corrections: [] };
  const platform: any = (window as any).app?.platform;
  const hold = await loadActiveHold(documentType, report.id, String(report.clientId));
  let corrections: TechnicalCorrectionRequest[] = [];
  try { corrections = platform?.technicalCorrectionService ? [...await platform.technicalCorrectionService.listByDocument(ctx(String(report.clientId)), documentType, report.id)] : []; } catch { corrections = []; }
  return { hold, corrections };
}

export function ndtHoldLocked(state: NdtEngineeringState): boolean {
  return !!state.hold;
}

/** Banners: HOLD (motivo + comentários da Engenharia) e correções pendentes com MARCAR COMO CORRIGIDO. */
export function ndtEngineeringBanners(state: NdtEngineeringState, canResolve: boolean): string {
  const target = typeof location !== "undefined" ? new URLSearchParams(location.search) : null;
  const highlightHold = !!target && target.get("section") === "hold";
  const highlightItem = target?.get("item") || null;
  return `${holdBanner(state.hold, highlightHold)}${pendingCorrectionsBanner(state.corrections, canResolve, highlightItem)}`;
}

export function ndtHoldButton(state: NdtEngineeringState, editable: boolean, buttonClass = "btn"): string {
  return holdButton(state.hold, editable, buttonClass);
}

export interface NdtEngineeringBindings {
  documentType: TechnicalDocumentType;
  report: any;
  state: NdtEngineeringState;
  /** Status de workflow atual do módulo (para guardar/restaurar no HOLD). */
  currentStatus: () => { status: string; label: string };
  summary: () => HoldDocumentSummary;
  /** Depois de HOLD / liberação / correção marcada: recarregar estado e re-renderizar. */
  onChanged: (message: string) => Promise<void> | void;
}

export function bindNdtEngineeringEvents(root: ParentNode, b: NdtEngineeringBindings): void {
  root.querySelector("#btn-put-on-hold")?.addEventListener("click", () => {
    openHoldModal({ documentType: b.documentType, documentId: b.report.id, summary: b.summary(), currentStatus: b.currentStatus() }, hold => { void b.onChanged(`Documento EM HOLD (${hold.summary.documentNumber}). Motivo registrado; a Engenharia foi avisada pelo Painel.`); });
  });
  root.querySelector("#btn-release-hold")?.addEventListener("click", () => {
    if (!b.state.hold) return;
    openHoldReleaseModal(b.state.hold, b.currentStatus(), hold => { void b.onChanged(`Documento liberado do HOLD. Status restaurado: ${hold.restoredStatus === b.currentStatus().status ? b.currentStatus().label : hold.previousStatusLabel}.`); });
  });
  root.querySelectorAll("[data-resolve-correction]").forEach(btn => btn.addEventListener("click", async () => {
    const id = (btn as HTMLElement).dataset["resolveCorrection"];
    if (!id) return;
    try {
      await (window as any).app.platform.technicalCorrectionService.markResolved(ctx(String(b.report.clientId)), id, "");
      await b.onChanged("Item marcado como corrigido. Quando terminar todas as correções, use \"Reenviar para o PLH\".");
    } catch (e: any) {
      await b.onChanged(e?.safeMessage || e?.message || "Erro ao marcar o item como corrigido.");
    }
  }));
}

/** Mapeia a seção da correção ("results", "photos"…) para o nº da seção da página pelo rótulo. */
export function ndtSectionNumberFor(sectionId: string, sections: ReadonlyArray<readonly [string, string]>): string | null {
  const keywords: Record<string, RegExp> = {
    general: /identifica|dados gerais/i,
    parameters: /par[aâ]metro|dados do ensaio|crit[eé]rio/i,
    equipment: /aparelho|equipamento|transdutor|calibra/i,
    consumables: /consum|materia/i,
    results: /resultado|registro|ponto|junta/i,
    croqui: /croqui/i,
    photos: /foto|evid/i,
    observations: /observa|parecer/i,
    signatories: /signat/i
  };
  const re = keywords[sectionId];
  if (!re) return null;
  const hit = sections.find(([, label]) => re.test(label));
  return hit ? hit[0] : null;
}

/** Depois do render: rola até a seção/item da correção (deep link) e destaca. */
export function ndtDeepLinkFocus(root: ParentNode & { querySelector: Element["querySelector"] }, prefix: string, sections: ReadonlyArray<readonly [string, string]>): void {
  applyDeepLinkFocus(root, {
    section: sectionId => { const num = ndtSectionNumberFor(sectionId, sections); return num ? `#${prefix}-sec-${num}` : null; },
    item: (itemId, sectionId) => {
      if (sectionId === "hold") return `.hold-banner`;
      const esc = itemId.replace(/["\\]/g, "\\$&");
      return `[data-item="${esc}"], [data-point-id="${esc}"], [data-result-item="${esc}"], [data-correction-id="${esc}"]`;
    }
  });
}

/*
 * A revisão ortográfica que ficava aqui (ndtApplySpelling / modal próprio) foi
 * aposentada em 2026-09-27: a ortografia é do INSPECT IA REVIEW, que reaproveita
 * o mesmo corretor (domain/text/spelling-corrector.ts) dentro da revisão
 * obrigatória antes do envio ao PLH. Dois motores de texto concorrentes não.
 */

export function canResolveCorrections(): boolean {
  return contextStore.hasPermission(PermissionCode.TechnicalReviewSubmitToPlh);
}
